import React from "react";
import { motion } from "framer-motion";
import useDocumentMeta from "../lib/useDocumentMeta";

const SECTIONS = [
    {
        title: "1. Acceptance of Terms",
        body: [
            "By accessing the Raj Graphics website, engaging our studio, or commissioning any design or signage service, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.",
        ],
    },
    {
        title: "2. Services",
        body: [
            "Raj Graphics provides graphic design, brand identity, packaging, signage and related creative services. The exact scope, deliverables and timeline of each engagement are confirmed in writing — typically over WhatsApp, email or a signed quote — before work begins.",
        ],
    },
    {
        title: "3. Quotes, Booking & Payment",
        body: [
            "All quotes are valid for 14 days unless stated otherwise. To begin a project we typically require an upfront booking fee (50% of total) with the balance payable before final files are released or signage is installed.",
            "Custom and enterprise engagements may follow a milestone‑based payment schedule outlined in the project agreement.",
        ],
    },
    {
        title: "4. Revisions & Scope",
        body: [
            "Each package includes a defined number of revision rounds. Additional rounds, scope expansions or out‑of‑brief requests are billed separately at a transparent per‑round rate which we'll share before any extra work is done.",
        ],
    },
    {
        title: "5. Approvals & Delivery",
        body: [
            "Final files, print artwork and installed signage are released only after written client approval at the relevant milestone. Once approved, any further changes are treated as a new round of work.",
        ],
    },
    {
        title: "6. Intellectual Property",
        body: [
            "On full and final payment, ownership of approved final deliverables (e.g. logo, identity files, packaging artwork) transfers to the client. Concepts, sketches and unused alternates remain the property of Raj Graphics and may be used in our portfolio unless explicitly agreed otherwise.",
            "We may showcase delivered work on our website, social media and case studies unless a confidentiality clause is mutually agreed in writing.",
        ],
    },
    {
        title: "7. Cancellations & Refunds",
        body: [
            "If a project is cancelled by the client after kickoff, the booking fee is non‑refundable as it covers initial discovery, concept development and reserved studio time. Work completed up to the cancellation date will be billed accordingly.",
            "If we are unable to deliver due to reasons solely on our end, we will offer a fair refund or alternative solution.",
        ],
    },
    {
        title: "8. Third‑Party Production",
        body: [
            "For physical signage, packaging printing and similar production work, the final result depends on third‑party vendors and substrates. While we supervise quality closely, slight tonal or material variations are inherent to physical production and are not grounds for refund.",
        ],
    },
    {
        title: "9. Limitation of Liability",
        body: [
            "Raj Graphics' total liability for any claim related to a project shall not exceed the fees paid by the client for that specific project. We are not liable for indirect, incidental or consequential losses, including loss of business, profits or data.",
        ],
    },
    {
        title: "10. Governing Law",
        body: [
            "These Terms are governed by the laws of India. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of Nagpur, Maharashtra.",
        ],
    },
    {
        title: "11. Updates to These Terms",
        body: [
            "We may update these Terms occasionally. Material changes will be reflected on this page with the updated effective date.",
        ],
    },
    {
        title: "12. Contact",
        body: [
            "Questions about these Terms? Reach out at hello@rajgraphics.in or call +91 89997 22515.",
        ],
    },
];

const TermsPage = () => {
    useDocumentMeta({
        title: "Terms & Conditions — Raj Graphics",
        description:
            "Terms & Conditions for engaging Raj Graphics studio for design, branding and signage services.",
    });

    return (
        <main data-testid="terms-page" className="bg-[#050505]">
            <section className="relative pt-36 pb-12 md:pt-40 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Legal
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                        data-testid="terms-headline"
                    >
                        Terms &amp; Conditions
                    </motion.h1>
                    <p className="mt-5 font-inter text-sm text-zinc-500">
                        Effective date: 01 January 2025 · Raj Graphics, Nagpur, India
                    </p>
                </div>
            </section>

            <section className="relative pb-24 md:pb-32 border-t border-white/5">
                <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 md:pt-16">
                    <div className="space-y-10" data-testid="terms-sections">
                        {SECTIONS.map((s) => (
                            <article key={s.title}>
                                <h2 className="font-poppins font-medium text-white text-xl md:text-2xl mb-4">
                                    {s.title}
                                </h2>
                                <div className="space-y-4">
                                    {s.body.map((p, idx) => (
                                        <p
                                            key={idx}
                                            className="font-inter text-zinc-400 leading-relaxed"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default TermsPage;
